/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, NgZone} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {StateService, UIRouter} from '@uirouter/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';
import template from './order-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {defaultCurrencyDeriver} from '../line-item/currency-default-calculator';
import {lineItemCommons} from '../line-item/line-item-commons';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {advertiserFromNavigation} from '../../components/util/navigation-context';
import {updateBudgets} from './order-budgets';
import {OrderBudgetModalService} from './order-budget-modal.service';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {UserResource} from '../../components/api/resources/resource/user.resource';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {SearchResource} from '../../components/api/resources/resource/search';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {compareById} from '../common/util/object-id-comparison';
import {NavigationHistoryService} from '../../components/util/navigation-history.service';
import {bufferTemplateValue, createBufferedValue} from '../common/util/template-state';

@Component({
  standalone: false,
  selector: 'adn-order-edit-page',
  template
})
export class OrderEditPageComponent extends AdminResourceEditPageBase {
  private _readOnlyDirect: boolean | undefined;

  @Input()
  set readOnly(value: boolean) {
    this._readOnlyDirect = value;
  }

  get readOnly(): boolean {
    return this._readOnlyDirect;
  }

  budgetResult: any = {};
  uiBudgets: any[] = [];
  currentBudgetPeriod: any;
  teams: any[] = [];
  users: any[] | null = null;
  advertiserResults: any[] = [];
  perms: any = {};
  objectiveFields: any = {};
  bidSpecificationFields: any = {};
  objectivesAsString: () => string;
  editingModelObjectivesAsString: () => string;
  /**
   * Same hydrate race as line-item: live translations can flip
   * "impressions" → "rendered impressions" mid-check (NG0100).
   */
  private readonly _objectivesAsString = createBufferedValue('');

  get displayObjectivesAsString(): string {
    const next = typeof this.objectivesAsString === 'function' ? this.objectivesAsString() : '';
    return bufferTemplateValue(this._objectivesAsString, next, {
      onCommit: () => this.cdr.markForCheck()
    });
  }

  moneyCurrency: any;
  updateCurrencyHook: any = {};

  isPureMarketplace = false;
  isMarketplace = false;
  isAxAdvertiser = false;
  advertiserInfo = false;
  chartCurrency: any;
  showOptimisation = false;
  showAttentionBiddingGoal = false;
  showTeams = false;
  allowsObjectives = false;
  v1Objectives = false;
  allowsRateLimits = false;
  rateLimitTypes: string[] = [];
  canSeeReports = false;
  primaryNetworkCurrency: any;
  blockSubmit = false;

  private workOutDefaultCurrency: any;
  private hasTimeUpdated = false;
  private orderCommons: any;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly $state: StateService,
    private readonly appLocale: AppLocaleService,
    private readonly translate: TranslateService,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(UserResource) private readonly User: any,
    @Inject(SearchResource) private readonly searchResource: any,
    private readonly orderBudgetModalService: OrderBudgetModalService,
    private readonly uiRouter: UIRouter,
    ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly navigationHistory: NavigationHistoryService
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.deferFormDirtyTracking();

    this.workOutDefaultCurrency = defaultCurrencyDeriver(this, localUserProfile);
    this.isPureMarketplace = LocalNetworkProfile.isPureMarketplacePlatform();
    this.isMarketplace = LocalNetworkProfile.isMarketplacePlatform();
    this.perms = LocalUserPermissions.getAllVisPermissions();
    this.canSeeReports = LocalUserPermissions.getAnyVisibility().RUN_REPORTS === true || this.perms.AX_ADVERTISER;
    this.primaryNetworkCurrency = LocalNetworkProfile.get('defaultCurrency');
    this.allowsObjectives = LocalNetworkProfile.isOrderObjectives();
    this.showOptimisation = LocalNetworkProfile.isOrderOptimisation(localUserProfile.get('username'));
    this.showAttentionBiddingGoal = LocalNetworkProfile.getNetworkTag() === 'b7462';
    this.showTeams = LocalNetworkInfo.obtain().getInfo('teamsCount') > 1 || !this.isPureMarketplace;

    this.orderCommons = lineItemCommons(this, this.translate, this.appLocale, LocalNetworkProfile, LocalUserPermissions, this.adnTimeShifter, 'editingModel');
    this.orderCommons.setObjectiveFields();
    this.orderCommons.setBidSpecificationFields();
    this.orderCommons.setObjectivesAsString();
    this.orderCommons.setAxAdvertiser();
    this.objectiveFields = this.orderCommons.objectiveFields || {};
    this.bidSpecificationFields = this.orderCommons.bidSpecificationFields || {};
    this.objectivesAsString = this.editingModelObjectivesAsString;
    this.isAxAdvertiser = this.orderCommons.isAxAdvertiser;

    if (this.isAxAdvertiser) {
      this.allowsRateLimits = LocalNetworkProfile.isMarketplaceAdvertiserRateLimits();
      this.rateLimitTypes = ['RENDERED_IMPRESSION', 'VISIBLE_IMPRESSION', 'VIEWABLE_IMPRESSION'];
    } else {
      this.allowsRateLimits = true;
      this.rateLimitTypes = ['IMPRESSION', 'RENDERED_IMPRESSION', 'VISIBLE_IMPRESSION', 'VIEWABLE_IMPRESSION', 'CLICK'];
    }

    this.Team.query({pageSize: 1001}, (page: any) => {
      // [] → results flips *ngFor on the team <select> mid-CD (NG0100).
      deferMarkForCheck(this.cdr, () => {
        const allTeams = page.results;
        const isViewAllObjects = this.LocalUserPermissions.hasNetwork('VIEW_ALL_OBJECTS');
        const teams = isViewAllObjects ? allTeams : _.filter(allTeams, (g: any) => {
          return this.LocalUserPermissions.isPartOf(g.network.tagId || g.network.id, g.id);
        });
        this.teams = teams;
        if (_.get(this.editingModel, ['team', 'id'])) {
          this.resolveTeamReference();
          if (this.editingModel.team) {
            this.workOutDefaultCurrency(null, this.editingModel.team, null);
          }
        }
        if (allTeams.length === 1 && this.teams.length === 1 && !this.editingModel.team) {
          this.editingModel.team = allTeams[0];
        }
        this.resumeFormDirtyTracking();
      });
    });

    this.User.query({minimal: true}, (page: any) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.users = page.results;
      });
    });
  }

  get showAdvertiserField(): boolean {
    return this.perms.MANAGE_ADVERTISING === true || this.perms.AX_ADVERTISER === true;
  }

  readonly compareById = compareById;

  editsMade(): void {
    this.markExternalFormDirty();
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  onTeamChange(): void {
    this.workOutDefaultCurrency(null, this.editingModel.team, null);
    this.onFieldChange();
  }

  onAdvertiserChange(): void {
    this.workOutDefaultCurrency(this.editingModel.advertiser, null, null);
    this.onFieldChange();
  }

  onBudgetObjectiveChange(): void {
    this.chartCurrency = _.get(this.editingModel, ['objectives', 'BUDGET', 'currency']) || this.chartCurrency;
    this.onFieldChange();
  }

  advertiserSearch = (searchElement: AdnSelectSearchElement): void => {
    this.searchResource.query({q: searchElement.search, types: 'Advertiser'}).then((data: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.advertiserResults = data.searchResults;
      });
    });
  };

  changedObjectiveEndDate = (): void => {
    if (!this.hasTimeUpdated && !_.isDate(this.editingModel.objectiveEndDate)) {
      const networkTimezone = this.LocalNetworkProfile.get('timeZone') || 'utc';
      this.editingModel.modObjectiveEndDate = moment(this.editingModel.modObjectiveEndDate)
        .tz(networkTimezone)
        .endOf('day')
        .toDate();
      this.hasTimeUpdated = true;
    }
    this.onFieldChange();
  };

  editBudgets = (): void => {
    this.orderBudgetModalService.open({
      modelObject: _.cloneDeep(this.editingModel),
      moneyCurrency: _.cloneDeep(this.moneyCurrency),
      objTranslation: 'Order'
    }).then((data: any) => {
      if (data.obj) {
        this.budgetResult = this.budgetResult || {};
        this.budgetResult.data = true;
        _.set(this.editingModel, ['budget'], _.get(data, ['obj', 'budget']));
        _.set(this.editingModel, ['budgetRollover'], _.get(data, ['obj', 'budgetRollover']));
        updateBudgets(this, data.obj);
        this.markExternalFormDirty();
      }
    });
  };

  protected afterInitEditingModel(): void {
    this.editingModel.objectives = this.editingModel.objectives || {};
    this.editingModel.optimiseFor = this.editingModel.optimiseFor || {eventType: null};
    this.orderCommons.setObjectiveFields();
    _.forEach(_.get(this.editingModel, 'objectives') || {}, (val: any, key: string) => {
      if (val > 0 || _.get(val, 'amount') > 0) {
        this.objectiveFields[key] = true;
      }
    });
    this.orderCommons.setObjectivesAsString();
    this.objectivesAsString = this.editingModelObjectivesAsString;
    this.advertiserInfo = !this.isPureMarketplace || !!this.isNew;
    this.blockSubmit = !!this.readOnly;
    this.chartCurrency = _.get(this.editingModel, ['objectives', 'BUDGET', 'currency'])
      || _.get(this.editingModel, ['budget', 0, 'amount', 'currency']);
    // Use API model objectives, not editingModel — setObjectiveFields adds a BUDGET placeholder.
    this.v1Objectives = !_.isEmpty(this.model?.objectives) || this.LocalNetworkProfile.getNetworkId() === '3d65a1';

    if (this.editingModel.objectiveStartDate) {
      this.editingModel.modObjectiveStartDate = this.editingModel.objectiveStartDate;
    }
    if (this.editingModel.objectiveEndDate) {
      this.editingModel.modObjectiveEndDate = this.editingModel.objectiveEndDate;
    }

    const advertiser = advertiserFromNavigation(this.navigationHistory, this.uiRouter.globals.params);
    if (advertiser) {
      this.editingModel.advertiser = advertiser;
    }
    if (_.get(this.editingModel, ['advertiser', 'id'])) {
      this.advertiserResults = [this.editingModel.advertiser];
      this.workOutDefaultCurrency(this.editingModel.advertiser, null, null);
    }
    this.resolveTeamReference();
  }

  onOrderCreativesAdvertiserFixed(saved: any): void {
    this.ngZone.run(() => {
      if (saved) {
        this.applySave(saved);
      }
    });
  }

  protected afterInitAfterSave(saveObj?: any): void {
    updateBudgets(this, saveObj);
    if (_.get(this.editingModel, ['optimiseFor', 'customEventType', 'id'])) {
      this.editingModel.optimiseFor.eventType = 'CUSTOM';
    }
    if (saveObj) {
      this.editingModel.team = saveObj.team;
      this.editingModel.advertiser = saveObj.advertiser;
      this.workOutDefaultCurrency(saveObj.advertiser, null, null);
      this.chartCurrency = _.get(saveObj, ['objectives', 'BUDGET', 'currency']) || this.chartCurrency;
    }
    this.resolveTeamReference();
    this.budgetResult = this.budgetResult || {};
    this.budgetResult.data = false;
  }

  protected onBeforeSubmit(): void {
    if (!_.get(this.editingModel.advertiser, 'id')) {
      this.editingModel.advertiser = null;
    }
    this.editingModel.objectiveStartDate = _.isDate(this.editingModel.modObjectiveStartDate)
      ? this.editingModel.modObjectiveStartDate
      : null;
    this.editingModel.objectiveEndDate = _.isDate(this.editingModel.modObjectiveEndDate)
      ? this.editingModel.modObjectiveEndDate
      : null;

    if (!_.get(this.editingModel, ['salespersonUser', 'name']) && _.get(this.editingModel, ['salespersonUser', 'id'])) {
      delete this.editingModel.salespersonUser;
    }
    if (!_.get(this.editingModel, ['adOpsUser', 'name']) && _.get(this.editingModel, ['adOpsUser', 'id'])) {
      delete this.editingModel.adOpsUser;
    }

    if (!_.get(this.editingModel, ['objectives', 'BUDGET', 'amount'])) {
      _.set(this.editingModel, ['objectives', 'BUDGET'], null);
    }

    if (_.get(this.editingModel, ['optimiseFor', 'eventType']) === 'CUSTOM') {
      this.editingModel.optimiseFor.customEventType = {id: '1q9qmf5z0b2ry1qd'};
    } else if (_.get(this.editingModel, ['optimiseFor', 'customEventType'])) {
      this.editingModel.optimiseFor.customEventType = null;
    }
  }

  private resolveTeamReference(): void {
    if (!this.editingModel?.team || !this.teams.length) {
      return;
    }
    const teamId = _.isString(this.editingModel.team)
      ? this.editingModel.team
      : this.editingModel.team.id;
    const match = _.find(this.teams, ['id', teamId]);
    if (match) {
      this.editingModel.team = match;
    }
  }
}
